import { useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/presentation/hooks/useAuth'
import { useDriverDetail } from '@/presentation/hooks/useDriverDetail'
import { useNavigateWithParams } from '@/presentation/hooks/useNavigateWithParams'
import { Period } from '@/core/shared/types/dashboard'
import { DriverDetailLayout } from '@/presentation/layouts/DriverDetailLayout'
import DriverDetailSkeleton from '@/presentation/components/driver-detail/Skeleton'
import DriverInfo from '@/presentation/components/driver-detail/DriverInfo'
import MetricsCards from '@/presentation/components/driver-detail/MetricsCards'
import PerformanceMetrics from '@/presentation/components/driver-detail/PerformanceMetrics'
import AlertsSection from '@/presentation/components/driver-detail/AlertsSection'

export default function DriverDetailPage() {
  const { driverId } = useParams<{ driverId: string }>()
  const { user } = useAuth()
  const { navigateWithParams } = useNavigateWithParams()
  const [searchParams] = useSearchParams()
  const periodFromUrl = (searchParams.get('period') as Period) || Period.Today

  const {
    driverData,
    vehicleInfo,
    documents,
    selectedPeriod,
    isLoading,
    hasError,
    error,
    handlePeriodChange,
    refreshData,
    currentPeriodData,
    previousPeriodData,
    performanceData,
  } = useDriverDetail({
    driverId: driverId || '',
    initialPeriod: periodFromUrl,
  })

  const handleWhatsAppClick = () => {
    const phoneNumber = driverData?.phone?.replace(/[^0-9]/g, '')
    if (phoneNumber) {
      window.open(`https://wa.me/55${phoneNumber}`, '_blank')
    }
  }

  const handleCallClick = () => {
    if (driverData?.phone) {
      window.location.href = `tel:${driverData.phone}`
    }
  }

  const handleFilterChange = (period: Period, customDates?: { startDate: string; endDate: string }) => {
    handlePeriodChange(period, customDates)
    navigateWithParams(window.location.pathname, { period })
  }

  const handleBack = () => {
    navigateWithParams('/dashboard')
  }

  if (!user?.id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background dark:bg-background">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2 text-foreground">
            Autenticação Necessária
          </h2>
          <p className="text-muted-foreground">
            Por favor, forneça um token de autenticação válido
          </p>
        </div>
      </div>
    )
  }

  if (isLoading && !driverData) {
    return (
      <DriverDetailLayout
        selectedPeriod={selectedPeriod}
        onPeriodChange={handleFilterChange}
        onRefresh={refreshData}
        onBack={handleBack}
        isRefreshing={isLoading}
      >
        <DriverDetailSkeleton />
      </DriverDetailLayout>
    )
  }

  if (hasError || !driverData) {
    return (
      <DriverDetailLayout
        selectedPeriod={selectedPeriod}
        onPeriodChange={handleFilterChange}
        onRefresh={refreshData}
        onBack={handleBack}
      >
        <div className="flex items-center justify-center p-6 min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2 text-foreground">
              Motorista não encontrado
            </h2>
            <p className="text-muted-foreground mb-4">
              {error || 'O motorista solicitado não foi encontrado'}
            </p>
            <Button onClick={handleBack}>Voltar ao Dashboard</Button>
          </div>
        </div>
      </DriverDetailLayout>
    )
  }

  return (
    <DriverDetailLayout
      selectedPeriod={selectedPeriod}
      onPeriodChange={handleFilterChange}
      onRefresh={refreshData}
      onBack={handleBack}
      isRefreshing={isLoading}
    >
      {isLoading && driverData ? (
        <DriverDetailSkeleton />
      ) : (
        <>
          <DriverInfo
            driverData={driverData}
            vehicleInfo={vehicleInfo}
            documents={documents}
            onWhatsAppClick={handleWhatsAppClick}
            onCallClick={handleCallClick}
          />

          <MetricsCards
            currentPeriodData={currentPeriodData}
            previousPeriodData={previousPeriodData}
            selectedPeriod={selectedPeriod}
          />

          <div className="flex flex-col gap-4">
            <h3 className="text-center text-lg font-semibold text-card-foreground">
              Desempenho
            </h3>
            <PerformanceMetrics performanceData={performanceData} />
            <AlertsSection
              currentPeriodData={currentPeriodData}
              previousPeriodData={previousPeriodData}
            />
          </div>
        </>
      )}
    </DriverDetailLayout>
  )
}
